import { Close as CloseIcon } from '@mui/icons-material';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Paper, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useDataProvider, useRecordContext } from 'react-admin';

import { fontFamilyMonospace } from 'layout/theme';
import { setupEcho } from '../../providers/sockets/laravel-echo';

interface TerminalFeedbackProps {
  /** Whether the terminal dialog is open */
  open: boolean;

  /** Function to call when closing the dialog */
  onClose: () => void;

  /** Unit hostname for WebSocket connection */
  hostname: string;

  /** Command execution UUID */
  executionUuid: string;

  /** Command execution ID for API calls */
  executionId: number;

  /** Command name for display */
  commandName: string;

  /** Machine-readable command slug (Command.name), used as the third segment of the CMD_ACK topic */
  commandSlug: string;

  /** Feedback configuration */
  feedbackConfig?: {
    wait_type: 'done_message' | 'topic_message';
    custom_topic?: string;
    custom_message?: string;
    show_terminal: boolean;
  };
}

/**
 * Terminal Feedback Dialog
 *
 * Displays real-time command output via WebSocket and monitors
 * for completion messages based on feedback configuration.
 */
export const TerminalFeedback: React.FC<TerminalFeedbackProps> = ({
  open,
  onClose,
  hostname,
  executionUuid,
  executionId,
  commandName,
  commandSlug,
  feedbackConfig,
}) => {
  const [output, setOutput] = useState<string[]>([]);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const outputRef = useRef<HTMLDivElement>(null);
  const channelRef = useRef<any>(null);
  const record = useRecordContext();
  const dataProvider = useDataProvider();

  // Auto-scroll to bottom when new output arrives
  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [output]);

  // Laravel Echo connection for real-time output
  useEffect(() => {
    if (!open || !executionUuid || !record?.id) return;

    // Prevent duplicate connections
    if (channelRef.current) {
      console.log('Connection already exists, skipping setup');
      return;
    }

    // Get auth token from localStorage
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication token not found');
      setOutput(['[Error] Authentication token not found']);
      return;
    }

    // Get waiting message directly to avoid dependency issues
    const getWaitingMessage = (): string => {
      if (!feedbackConfig) {
        return 'Waiting for command completion...';
      }

      if (feedbackConfig.wait_type === 'done_message') {
        return `Waiting for done message on CMD_ACK/${hostname}/${commandSlug}`;
      } else if (feedbackConfig.wait_type === 'topic_message') {
        const topic = feedbackConfig.custom_topic?.replace('{hostname}', hostname) || 'custom topic';
        return `Waiting for message on ${topic}`;
      }

      return 'Waiting for command completion...';
    };

    // Fetch historical ACK messages first
    const loadHistoricalOutput = async (): Promise<{ output: string[]; isCompleted: boolean }> => {
      try {
        const ackResponse = await dataProvider.getCommandExecutionAckMessage(record.id, executionId);
        console.log('Raw ACK response:', ackResponse);
        const historicalOutput: string[] = [];
        let isCompleted = false;

        if (ackResponse?.data?.feedback_output) {
          // Split the feedback_output by lines and add each as a historical entry
          const lines = ackResponse.data.feedback_output.split('\n').filter((line: string) => line.trim() !== '');
          lines.forEach((line: string) => {
            historicalOutput.push(`[History] ${line}`);
          });
        }

        // Check if command is already completed
        if (ackResponse?.data?.feedback_completed) {
          isCompleted = true;
          historicalOutput.push('[Complete] Command finished successfully');
        }

        return { output: historicalOutput, isCompleted };
      } catch (error) {
        console.log('No historical output available or error fetching:', error);
        return { output: [], isCompleted: false };
      }
    };

    // Guards state updates once the dialog closes / the effect re-runs mid-flight,
    // consistent with the cleanup function below.
    let cancelled = false;

    // Header lines are set once, immediately, and never re-derived by a history re-sync.
    const headerLines = ['[Connected] Monitoring command: ' + commandName, '[Waiting] ' + getWaitingMessage()];

    // `historyLines`/`historyIsCompleted` hold the latest authoritative transcript from
    // the backend; `liveLines` holds events appended since that snapshot was applied.
    // A history (re)sync replaces both `historyLines` and clears `liveLines`, because the
    // fresh snapshot already contains everything the backend broadcast up to that point
    // (DB write precedes the queued broadcast) — no per-line de-duplication is done or wanted.
    let historyLines: string[] = [];
    let historyIsCompleted = false;
    let liveLines: string[] = [];

    // Gates `.command.feedback` events while a history (re)sync is in flight, so a
    // broadcast landing between subscribing and the history response is never lost:
    // the upcoming snapshot will already include it. Events arriving in that window are
    // dropped outright, not queued — the sync never replays anything (see above), so
    // there is nothing to keep.
    let awaitingHistorySync = true;

    // `syncHistory` re-runs on every `channel.subscribed(...)` firing (initial connect
    // and every later reconnect). Two firings can overlap — a reconnect landing while the
    // previous sync's HTTP request is still in flight is a realistic case, not an edge
    // case, since both are symptoms of the same flaky network. `syncGeneration` makes a
    // stale call's response a no-op instead of letting it stomp a newer, more
    // authoritative sync that already applied its own snapshot.
    let syncGeneration = 0;

    const renderOutput = () => {
      if (cancelled) return;
      const separator =
        historyLines.length > 0 && !historyIsCompleted
          ? ['[Info] --- Historical output above, live output below ---']
          : [];
      setOutput([...headerLines, ...historyLines, ...separator, ...liveLines]);
    };

    // Branches only on the two event types whose meaning changes component state
    // (`completion`, `error`). Everything else — the documented `output`, `success`
    // and `info` types, and any type invented server-side in the future — falls
    // through the default branch and is still rendered as a plain output line,
    // rather than silently discarded.
    const applyFeedbackEvent = (event: any) => {
      console.log('Feedback received:', event);

      switch (event.type) {
        case 'completion':
          if (!cancelled) setIsComplete(true);
          liveLines = [...liveLines, `[Complete] ${event.message || 'Command finished successfully'}`];
          break;
        case 'error':
          if (!cancelled) setError(event.message);
          liveLines = [...liveLines, `[Error] ${event.message}`];
          break;
        default:
          liveLines = [...liveLines, event.message || ''];
          break;
      }

      renderOutput();
    };

    // Re-fetches and re-applies the historical transcript. Run once the channel
    // subscription is confirmed, and again on every later `subscribed` (websocket
    // reconnects) — see the `channel.subscribed(...)` registration below.
    const syncHistory = async () => {
      const generation = ++syncGeneration;
      awaitingHistorySync = true;
      liveLines = [];

      const historicalData = await loadHistoricalOutput();
      // A newer sync may have started (and possibly already finished) while this one
      // was in flight — if so, its result is stale and applying it would stomp the
      // newer, more authoritative snapshot. No-op instead.
      if (cancelled || generation !== syncGeneration) return;
      console.log('Historical output loaded:', historicalData);

      historyLines = historicalData.output;
      historyIsCompleted = historicalData.isCompleted;
      if (historicalData.isCompleted) {
        setIsComplete(true);
      }

      renderOutput();
      awaitingHistorySync = false;
    };

    // Setup Echo and subscribe to channel
    const initializeTerminal = async () => {
      try {
        // Reset state when starting initialization
        setIsComplete(false);
        setError(null);
        renderOutput();

        // Setup WebSocket connection
        await setupEcho(token);
        if (cancelled) return;

        // Only set up channel if we don't already have a connection
        if (!channelRef.current) {
          // Subscribe to private channel for this command execution
          // @ts-ignore
          const channel = window.Echo.private(`command-feedback.${executionUuid}`);
          channelRef.current = channel;

          // Listen for command feedback events immediately, so nothing broadcast after
          // this point is missed — events are dropped (not queued) until a history sync
          // lands, since that snapshot will already include them.
          channel.listen('.command.feedback', (event: any) => {
            if (cancelled) return;
            if (awaitingHistorySync) return;
            applyFeedbackEvent(event);
          });

          // `hasConfirmedOnce` distinguishes the very first confirmation — whose failure
          // is surfaced by rejecting `subscriptionConfirmed` below, caught by this
          // function's own try/catch — from a later one (a reconnect after we were
          // already up), whose failure would otherwise reject an already-settled promise
          // and get silently swallowed. Post-first-success errors are surfaced directly.
          let hasConfirmedOnce = false;

          // Resolves once the subscription is confirmed; also fires the history
          // (re)sync on every future confirmation, which covers reconnects since Pusher
          // re-fires `pusher:subscription_succeeded` after every resubscribe.
          const subscriptionConfirmed = new Promise<void>((resolve, reject) => {
            channel.subscribed(() => {
              if (cancelled) return;
              hasConfirmedOnce = true;
              // Clear a subscription-error banner from an earlier failed attempt now
              // that we are (re)connected.
              setError(null);
              resolve();
              syncHistory();
            });
            channel.error((err: unknown) => {
              if (cancelled) return;
              console.error('Echo channel subscription error:', err);
              if (hasConfirmedOnce) {
                setError('Connection error occurred');
                setOutput((prev) => [...prev, '[Error] Failed to connect to server']);
                return;
              }
              reject(err);
            });
          });

          // Wait for the FIRST confirmation before initializeTerminal resolves, so a
          // subscription failure is caught below the same way a setupEcho failure is.
          await subscriptionConfirmed;
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to setup terminal:', err);
        setError('Connection error occurred');
        setOutput((prev) => [...prev, '[Error] Failed to connect to server']);
      }
    };

    initializeTerminal();

    // Cleanup on unmount
    return () => {
      console.log('Cleaning up Echo channel');
      cancelled = true;
      if (channelRef.current) {
        channelRef.current.stopListening('.command.feedback');
        // @ts-ignore
        window.Echo?.leave(`command-feedback.${executionUuid}`);
        channelRef.current = null;
      }
    };
  }, [open, executionUuid, executionId, commandName, commandSlug, hostname, feedbackConfig, record?.id, dataProvider]);

  const handleClose = () => {
    // Unsubscribe from Echo channel
    if (channelRef.current) {
      channelRef.current.stopListening('.command.feedback');
      // @ts-ignore
      window.Echo?.leave(`command-feedback.${executionUuid}`);
      channelRef.current = null;
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullScreen>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            Tovertafel: {record?.serial} / Command Feedback: {commandName}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Paper
          ref={outputRef}
          sx={{
            'backgroundColor': '#1e1e1e',
            'color': '#d4d4d4',
            'fontFamily': fontFamilyMonospace,
            'fontSize': '14px',
            'padding': 2,
            'flex': 1,
            'overflow': 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#2d2d2d',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#555',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#777',
            },
          }}
        >
          {output.length === 0 ? (
            <Typography sx={{ color: '#888' }}>Connecting...</Typography>
          ) : (
            <>
              {console.log('Rendering output:', output)}
              {output.map((line, index) => (
                <Box
                  key={index}
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    mb: 0.5,
                    color: line.startsWith('[Error]')
                      ? '#f48771'
                      : line.startsWith('[Complete]')
                        ? '#4ec9b0'
                        : line.startsWith('[Connected]') || line.startsWith('[Waiting]')
                          ? '#569cd6'
                          : line.startsWith('[History]')
                            ? '#dcdcaa'
                            : line.startsWith('[Info]')
                              ? '#9cdcfe'
                              : '#d4d4d4',
                  }}
                >
                  {line}
                </Box>
              ))}
            </>
          )}
        </Paper>

        {isComplete && (
          <Box mt={2} p={2} bgcolor="success.light" borderRadius={1}>
            <Typography variant="body2" color="success.dark">
              ✓ Command completed successfully
            </Typography>
          </Box>
        )}

        {error && (
          <Box mt={2} p={2} bgcolor="error.light" borderRadius={1}>
            <Typography variant="body2" color="error.dark">
              ✗ {error}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
